/**
 * PortfolioScreen — the Portfolio analytics page (Story UI-U7).
 *
 * Sebastian's "Statistiken / Portfolio" area, LIVE off GET /statistics/portfolio: a KPI
 * header, a stat strip, the Prämienentwicklung chart, a Sparten donut, the Renewal-Pipeline
 * (its own /renewals/heute wire), Top-Kunden and Letzte Aktivitäten. A read surface — no
 * Primary action (links/ghost only). Design-contract conformant: tokens only, color =
 * signal, mono for money/IDs, display-font H1. Renders correctly in light AND dark mode.
 *
 * Defensive rendering (coordination contract): null money → `—`; null Automatisierungsgrad
 * → tile hidden; the optional gaps_count / ki_beobachtungen (ABSENT today) light up their
 * element ONLY when present — feature-detected, never stubbed.
 *
 * [Source: apps/web/src/features/dashboard/DashboardScreen.tsx] (layout + activity)
 * [Source: docs/stories/ui-u7-portfolio.md AC 2/3/4/5/6/7 + Dev Notes "Page layout"]
 */

import { usePortfolioStatistics } from './hooks/usePortfolioStatistics';
import { dateTimeShortDe } from '../../lib/format';
import { KpiHeader } from './components/KpiHeader';
import { StatStrip } from './components/StatStrip';
import { PraemienChart } from './components/PraemienChart';
import { SpartenDonut } from './components/SpartenDonut';
import { RenewalPipeline } from './components/RenewalPipeline';
import { TopKunden } from './components/TopKunden';
import { LetzteAktivitaeten } from './components/LetzteAktivitaeten';
import { KiBeobachtungen } from './components/KiBeobachtungen';
import { KontrollbereichPanel } from '../control/KontrollbereichPanel';
import { TerminationLettersPanel } from '../mandate/termination/TerminationLettersPanel';

export function PortfolioScreen() {
  const { data, isLoading, isError } = usePortfolioStatistics();

  if (isError || (!data && !isLoading)) {
    return (
      <div className="px-6 py-5">
        <h1 className="display-title">Portfolio</h1>
        <div className="mt-6 grid place-items-center rounded-xl border border-border p-12 text-center text-sm text-muted-foreground">
          Portfolio-Daten konnten nicht geladen werden.
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-6 py-5">
        <h1 className="display-title">Portfolio</h1>
        <div className="mt-6 grid place-items-center rounded-xl border border-border p-12 text-center text-sm text-muted-foreground">
          Lade Portfolio …
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-5">
      {/* H1 + as_of subline */}
      <div className="mb-5">
        <h1 className="display-title">Portfolio</h1>
        <p className="mt-1 text-xs text-muted-foreground">Stand: {dateTimeShortDe(data.as_of)} Uhr</p>
      </div>

      {/* KPI header */}
      <div className="mb-4">
        <KpiHeader kpis={data.kpis} />
      </div>

      {/* Stat strip (GAPs tile appears only when gaps_count is present) */}
      <div className="mb-6">
        <StatStrip stats={data.stats} gapsCount={data.gaps_count} />
      </div>

      {/* Kontrollbereich Cockpit (Bereich 5, Kapitel 8) — below the KPI tiles; hidden
          without administration.kontrolle.rueckstand.einsehen. */}
      <div className="mb-6">
        <KontrollbereichPanel />
      </div>

      {/* P6-S3: the Dankesbriefe of Mandatsauflösungen still open (read-only; hidden when none) */}
      <TerminationLettersPanel />

      {/* Prämienentwicklung — full width */}
      <div className="mb-6">
        <PraemienChart series={data.praemien_entwicklung} />
      </div>

      {/* Sparten + Renewal-Pipeline */}
      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <SpartenDonut sparten={data.sparten} />
        <RenewalPipeline />
      </div>

      {/* Top-Kunden (2 cols) + Letzte Aktivitäten (1 col) */}
      <div className="mb-6 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <TopKunden kunden={data.top_kunden} />
        </div>
        <LetzteAktivitaeten items={data.letzte_aktivitaeten} />
      </div>

      {/* KI-Beobachtungen — feature-detected (ABSENT today) */}
      {data.ki_beobachtungen !== undefined && data.ki_beobachtungen.length > 0 && (
        <div className="mb-6">
          <KiBeobachtungen items={data.ki_beobachtungen} />
        </div>
      )}
    </div>
  );
}
